import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { loadStripe } from '@stripe/stripe-js';
import {
  FaCheckCircle,
  FaRocket,
  FaUsers,
  FaShieldAlt,
  FaGraduationCap,
  FaCalendarAlt,
  FaPlay,
  FaTrophy,
  FaCheck,
  FaHeadset,
  FaVideo,
  FaBook,
  FaChartLine,
  FaCogs,
  FaLaptopCode,
  FaBolt,
  FaQuestionCircle,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaClock
} from 'react-icons/fa';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';
import useCourseData from '../../hooks/useCourseData';
import useCheckoutRedirect from '../../hooks/useCheckoutRedirect';
import useSavedPaymentMethods from '../../hooks/useSavedPaymentMethods';
import { ConfirmationModal } from '../../components/UnifiedModal';
import useUser from '../../hooks/useUser';
import fetchWithTimeout from '../../utils/fetchWithTimeout';
import './BootcampUpsellPage.css';

// Initialize Stripe for payment confirmation
const stripePublishableKey = process.env.REACT_APP_STRIPE_PK || '';
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;
const PAYMENT_INTENT_FETCH_TIMEOUT_MS = 15000;
const BOOTCAMP_PRICING_FETCH_TIMEOUT_MS = 10000;

/**
 * Post-purchase upsell page shown after Starter plan checkout.
 *
 * WHY THIS PAGE EXISTS:
 * Research shows post-purchase upsells convert 4-15% because customers are
 * already in a buying mindset. This page captures that moment to offer the
 * AI Trading Bootcamp.
 *
 * DATA SOURCES:
 * - Pricing: /stripe/products-pricing?slugs=ai-bootcamp
 * - Payment plans: /checkout/catalog (via useCheckoutRedirect)
 * - Cohort dates: useCourseData() hook
 *
 * USER FLOW:
 * 1. User completes Starter checkout → magic link login
 * 2. Redirect lands here instead of directly to AI Agent
 * 3. User either accepts upsell → bootcamp checkout
 *    OR declines → continues to AI Agent (original destination)
 */
function BootcampUpsellPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch real bootcamp pricing from API
  const [pricingInfo, setPricingInfo] = useState(null);
  const [isLoadingPricing, setIsLoadingPricing] = useState(true);

  // Payment plans state (fetched from /checkout/catalog)
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [selectedPlanKey, setSelectedPlanKey] = useState(null); // Set after plans load
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);

  // Countdown timer state (15 minutes from page load for urgency - research shows optimal for post-purchase upsells)
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Track page load time for analytics (time on page calculation)
  const [pageLoadTime] = useState(() => Date.now());

  // PROMO CODE for upsell - MUST exist in Stripe for the discount to apply
  // This promo code should be created in Stripe Dashboard with the appropriate discount
  // NOTE: Stripe doesn't allow underscores in promo codes, so we use NEWSUBSCRIBERBOOTCAMP
  // DEV: Can override via URL param ?promoCode=DEVTEST99OFF for testing
  const UPSELL_PROMO_CODE = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('promoCode') || 'NEWSUBSCRIBERBOOTCAMP';
  }, [location.search]);

  // Get cohort date info and bootcamp data from course data hook (same as AIBootcampSalesPage)
  const { nextSessionDate, bootcampData, getWeekDate, isLoading: isCourseLoading } = useCourseData(true);

  // Use checkout redirect hook for starting bootcamp checkout (fallback if one-click fails)
  const { startCheckout, processing: checkoutProcessing } = useCheckoutRedirect('bootcamp_upsell');

  // One-click checkout with saved payment method
  // Research shows one-click with confirmation modal has optimal conversion + low chargebacks
  // user.email is required for backend to find the correct Stripe customer (prevents "already attached" error)
  const { isAuthenticated, getApiToken, user } = useUser();
  const { paymentMethods, loading: loadingPaymentMethods, hasMethods } = useSavedPaymentMethods();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [oneClickProcessing, setOneClickProcessing] = useState(false);
  const [oneClickError, setOneClickError] = useState(null);

  // Sticky CTA for mobile - shows when CTA card is below viewport
  const ctaCardRef = useRef(null);
  const [showStickyCta, setShowStickyCta] = useState(true);

  // Get the default/first saved payment method for one-click
  const defaultPaymentMethod = useMemo(() => {
    if (!paymentMethods || paymentMethods.length === 0) return null;
    // Find default card, or use first one
    return paymentMethods.find(pm => pm.isDefault) || paymentMethods[0];
  }, [paymentMethods]);

  // Parse redirect destination from URL params (preserved from checkout flow)
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const redirectParam = queryParams.get('redirect') || '/ai-agent';
  const leadIdParam = queryParams.get('leadId');
  const variantParam = queryParams.get('variant');

  // Build the final redirect URL (used when user skips upsell). The default is
  // /ai-agent, but the caller (StarterCheckoutPage) may pass a deeper destination
  // via ?redirect=... — e.g. a marketplace strategy detail page with its own
  // resumeAction query params for auto-resume. We need to MERGE leadId/variant
  // into whatever query string the caller already put on the redirect path,
  // not concatenate with an extra "?" that breaks URL parsing.
  const skipRedirectUrl = useMemo(() => {
    const basePath = redirectParam.startsWith('/') ? redirectParam : '/ai-agent';
    const parsed = new URL(basePath, window.location.origin);
    if (leadIdParam) parsed.searchParams.set('leadId', leadIdParam);
    if (variantParam) parsed.searchParams.set('variant', variantParam);
    return `${parsed.pathname}${parsed.search}`;
  }, [redirectParam, leadIdParam, variantParam]);

  const skipRedirectLabel = useMemo(() => {
    if (
      skipRedirectUrl.startsWith('/deploy') ||
      skipRedirectUrl.includes('resumeAction=startTrading')
    ) {
      return 'No thanks, continue to deploy my bot →';
    }
    if (skipRedirectUrl.startsWith('/ai-agent')) {
      return 'No thanks, take me to AI Agent →';
    }
    return 'No thanks, continue →';
  }, [skipRedirectUrl]);

  // Fetch real pricing from API (same pattern as AIBootcampSalesPage)
  useEffect(() => {
    const fetchPricing = async () => {
      setIsLoadingPricing(true);
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'https://api.botspot.trade';
        const response = await fetchWithTimeout(
          `${API_URL}/stripe/products-pricing?slugs=ai-bootcamp`,
          {},
          BOOTCAMP_PRICING_FETCH_TIMEOUT_MS,
        );
        if (response.ok) {
          const data = await response.json();
          console.log('[BootcampUpsellPage] Pricing data received:', data);
          setPricingInfo(data);
        }
      } catch (error) {
        console.error('[BootcampUpsellPage] Error fetching pricing:', error);
      } finally {
        setIsLoadingPricing(false);
      }
    };
    fetchPricing();
  }, []);

  // Fetch payment plans from catalog API with the upsell promo code
  // This ensures we show the correct discounted pricing for new subscribers
  useEffect(() => {
    const fetchPaymentPlans = async () => {
      setIsLoadingPlans(true);
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'https://api.botspot.trade';
        // Pass the promo code to get accurate discounted pricing from Stripe
        const response = await fetchWithTimeout(
          `${API_URL}/checkout/catalog?promoCode=${encodeURIComponent(UPSELL_PROMO_CODE)}`,
          {},
          BOOTCAMP_PRICING_FETCH_TIMEOUT_MS,
        );
        if (response.ok) {
          const data = await response.json();
          console.log('[BootcampUpsellPage] Catalog data received (with promo code):', {
            promoCodeUsed: UPSELL_PROMO_CODE,
            data,
          });

          // Find ai-bootcamp product and extract plans
          const bootcampProduct = data.products?.find(p => p.slug === 'ai-bootcamp');
          if (bootcampProduct?.plans && Array.isArray(bootcampProduct.plans)) {
            // Plans is already an array with planKey in each object
            const plansArray = [...bootcampProduct.plans];

            // Sort plans: upfront first, then by number of payments
            plansArray.sort((a, b) => {
              if (a.planKey === 'bootcamp-upfront') return -1;
              if (b.planKey === 'bootcamp-upfront') return 1;
              // Extract number from planKey (e.g., bootcamp-3mo -> 3)
              const aNum = parseInt(a.planKey?.match(/\d+/)?.[0] || '99');
              const bNum = parseInt(b.planKey?.match(/\d+/)?.[0] || '99');
              return aNum - bNum;
            });

            setPaymentPlans(plansArray);

            // DEBUG: Log detailed pricing info to verify correct promo code is applied
            console.log('[BootcampUpsellPage] 💰 PRICING DEBUG:', {
              promoCodeRequested: UPSELL_PROMO_CODE,
              plans: plansArray.map(plan => ({
                planKey: plan.planKey,
                amount: plan.amount,
                discountedAmount: plan.discountedAmount,
                hasDiscount: typeof plan.discountedAmount === 'number' && plan.discountedAmount < plan.amount,
                appliedPromotion: plan.appliedPromotion,
              })),
            });

            // CRITICAL: Set default to 5-month plan IMMEDIATELY after loading
            // This ensures 5-month is always selected on page load
            setSelectedPlanKey('bootcamp-5mo');
          }
        }
      } catch (error) {
        console.error('[BootcampUpsellPage] Error fetching payment plans:', error);
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchPaymentPlans();
  }, [UPSELL_PROMO_CODE]);

  // Track page view on mount
  useEffect(() => {
    // Get UTM parameters from URL
    const searchParams = new URLSearchParams(location.search);
    const utmSource = searchParams.get('utm_source');
    const utmMedium = searchParams.get('utm_medium');
    const utmCampaign = searchParams.get('utm_campaign');

    pushEvent({
      event: 'upsell_page_viewed',
      upsell_product: 'ai-bootcamp',
      source: 'post_starter_checkout',
      redirect_destination: skipRedirectUrl,
      page_path: location.pathname,
      promo_code: UPSELL_PROMO_CODE,
      ...(utmSource && { utm_source: utmSource }),
      ...(utmMedium && { utm_medium: utmMedium }),
      ...(utmCampaign && { utm_campaign: utmCampaign }),
    });
  }, [skipRedirectUrl, location.search, location.pathname]);


  // Initialize countdown timer (15 minutes from page load)
  // Research shows 10-15 minutes is optimal for immediate post-purchase upsells:
  // - Shopping momentum highest in first 60 seconds after purchase
  // - 15 minutes creates urgency without feeling pushy
  // - 24 hours loses urgency ("I'll decide later")
  useEffect(() => {
    // Check if we have a stored expiry time, otherwise create one
    // Key includes duration so changing timer duration resets for all users
    const storageKey = 'bootcamp_upsell_expiry_15m';
    let expiryTime = localStorage.getItem(storageKey);

    if (!expiryTime) {
      // Set expiry to 15 minutes from now (optimal for post-purchase upsells)
      expiryTime = Date.now() + (15 * 60 * 1000);
      localStorage.setItem(storageKey, expiryTime.toString());
    } else {
      expiryTime = parseInt(expiryTime, 10);
    }

    const updateTimer = () => {
      const now = Date.now();
      const remaining = expiryTime - now;

      if (remaining <= 0) {
        setTimeRemaining({ hours: 0, minutes: 0, seconds: 0, expired: true });
        return;
      }

      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      setTimeRemaining({ hours, minutes, seconds, expired: false });
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, []);

  // Set default selection to 5-month plan after plans load (lower barrier to entry)
  useEffect(() => {
    if (paymentPlans.length > 0 && !selectedPlanKey) {
      // Find the 5-month plan
      const fiveMonthPlan = paymentPlans.find(p => p.planKey === 'bootcamp-5mo');
      if (fiveMonthPlan) {
        setSelectedPlanKey('bootcamp-5mo');
      } else {
        // Fallback to first plan if 5-month doesn't exist
        setSelectedPlanKey(paymentPlans[0].planKey);
      }
    }
  }, [paymentPlans, selectedPlanKey]);

  // Simple scroll-based sticky CTA for mobile
  // Show sticky when CTA card is below viewport, hide when it's visible
  useEffect(() => {
    const handleScroll = () => {
      if (!ctaCardRef.current) return;
      if (window.innerWidth > 767) {
        setShowStickyCta(false);
        return;
      }

      const rect = ctaCardRef.current.getBoundingClientRect();
      // Show sticky if CTA card top is below the viewport
      // Hide sticky once CTA card is visible (top is in view)
      setShowStickyCta(rect.top > window.innerHeight);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll(); // Check initial state

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Helper to get bootcamp price from API data
  const getBootcampPrice = useCallback(() => {
    if (!pricingInfo?.products) {
      return { current: null, original: null, isDiscounted: false, isLoading: true };
    }

    const product = pricingInfo.products.find(p => p.slug === 'ai-bootcamp');
    if (!product) {
      return { current: null, original: null, isDiscounted: false, isLoading: false };
    }

    const currentPrice = product.discountedPrice || product.basePrice;
    const originalPrice = product.basePrice;
    const isDiscounted = !!product.discountedPrice;

    return {
      current: currentPrice?.amount ? `$${(currentPrice.amount / 100).toLocaleString()}` : null,
      original: isDiscounted && originalPrice?.amount ? `$${(originalPrice.amount / 100).toLocaleString()}` : null,
      isDiscounted,
      isLoading: false
    };
  }, [pricingInfo]);

  // Helper to format price (same pattern as BootcampPricingSection)
  const formatPrice = useCallback((amountCents) => {
    if (!amountCents) return '$0';
    const amount = amountCents / 100;
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }, []);

  // Helper to get pricing details for a specific plan
  // Uses REAL API data from paymentPlans - no hardcoded prices
  const getPlanPricingDetails = useCallback((planKey) => {
    const plan = paymentPlans.find(p => p.planKey === planKey);
    if (!plan) return null;

    // Use installments from plan data (same pattern as BootcampPricingSection)
    const installments = plan.installments || plan.metadata?.installments || 1;
    const isUpfront = installments === 1;

    // Get real pricing from API (amount is in CENTS from Stripe)
    const currentAmountCents = plan.discountedAmount ?? plan.amount;
    const originalAmountCents = plan.amount;
    const hasDiscount = plan.discountedAmount !== null && plan.discountedAmount !== undefined && plan.discountedAmount !== plan.amount;

    // Convert to dollars for calculations (formatPrice expects cents, so pass cents directly)
    const currentDollars = currentAmountCents / 100;
    const originalDollars = originalAmountCents / 100;

    if (isUpfront) {
      return {
        mainPrice: formatPrice(currentAmountCents), // Pass CENTS - formatPrice converts to dollars
        originalPrice: hasDiscount ? formatPrice(originalAmountCents) : null,
        subtext: 'One-time payment • Lifetime access',
        isDiscounted: hasDiscount,
        perPayment: null,
        totalPayments: 1,
        installments: 1,
        savings: hasDiscount ? originalDollars - currentDollars : null,
        discountPercent: hasDiscount ? Math.round((originalDollars - currentDollars) / originalDollars * 100) : 0
      };
    } else {
      // Installment plan - use real API prices
      const totalAmountCents = currentAmountCents * installments;
      const originalTotalCents = originalAmountCents * installments;

      return {
        mainPrice: `${formatPrice(currentAmountCents)}/mo`, // Pass CENTS
        originalPrice: hasDiscount ? `${formatPrice(originalAmountCents)}/mo` : null,
        subtext: `${installments} monthly payments (${formatPrice(totalAmountCents)} total)`,
        isDiscounted: hasDiscount,
        perPayment: formatPrice(currentAmountCents),
        totalPayments: installments,
        installments: installments,
        savings: hasDiscount ? (originalTotalCents - totalAmountCents) / 100 : null,
        discountPercent: hasDiscount ? Math.round((originalDollars - currentDollars) / originalDollars * 100) : 0
      };
    }
  }, [paymentPlans, formatPrice]);

  // Handle accept upsell - show confirmation modal for one-click checkout if user has saved cards
  // Research shows one-click with confirmation modal has 9.8% conversion with only 5% refunds
  // vs pure one-click (10% conversion, 15% refunds) - higher net revenue with modal
  const handleAcceptUpsell = useCallback(() => {
    setOneClickError(null);

    // If user has saved payment methods, show confirmation modal for one-click checkout
    if (isAuthenticated && hasMethods && defaultPaymentMethod) {
      setShowConfirmModal(true);

      // Track modal shown
      pushEvent({
        event: 'upsell_confirmation_modal_shown',
        upsell_product: 'ai-bootcamp',
        upsell_plan: selectedPlanKey,
        payment_method_brand: defaultPaymentMethod.brand,
        payment_method_last4: defaultPaymentMethod.last4,
        source: 'post_starter_checkout',
      });
      return;
    }

    // Fallback: No saved payment methods - redirect to full checkout
    const planDetails = getPlanPricingDetails(selectedPlanKey);
    const timeOnPageSeconds = Math.round((Date.now() - pageLoadTime) / 1000);

    pushEvent({
      event: 'upsell_accepted',
      upsell_product: 'ai-bootcamp',
      upsell_price: planDetails?.mainPrice,
      upsell_plan: selectedPlanKey,
      upsell_installments: planDetails?.installments,
      promo_code: UPSELL_PROMO_CODE,
      source: 'post_starter_checkout',
      time_on_page_seconds: timeOnPageSeconds,
      redirect_destination: skipRedirectUrl,
      checkout_type: 'redirect', // Track that this is fallback redirect checkout
    });

    // Use useCheckoutRedirect to start checkout with selected payment plan
    startCheckout({
      products: [{ productSlug: 'ai-bootcamp', planKey: selectedPlanKey }],
      promoCode: UPSELL_PROMO_CODE,
      flowContext: 'bootcamp_upsell',
      metadata: {
        origin: {
          flow: 'bootcamp_upsell',
          source: 'post_starter_checkout',
          returnTo: skipRedirectUrl,
        },
      },
    });
  }, [getPlanPricingDetails, selectedPlanKey, startCheckout, skipRedirectUrl, pageLoadTime, isAuthenticated, hasMethods, defaultPaymentMethod]);

  // Handle one-click checkout confirmation - process payment with saved card
  const handleOneClickConfirm = useCallback(async () => {
    if (!defaultPaymentMethod) {
      setOneClickError('No saved payment method available');
      return;
    }

    setOneClickProcessing(true);
    setOneClickError(null);
    const planDetails = getPlanPricingDetails(selectedPlanKey);
    const timeOnPageSeconds = Math.round((Date.now() - pageLoadTime) / 1000);

    // Track one-click attempt
    pushEvent({
      event: 'upsell_one_click_attempted',
      upsell_product: 'ai-bootcamp',
      upsell_price: planDetails?.mainPrice,
      upsell_plan: selectedPlanKey,
      payment_method_brand: defaultPaymentMethod.brand,
      payment_method_last4: defaultPaymentMethod.last4,
      time_on_page_seconds: timeOnPageSeconds,
      source: 'post_starter_checkout',
    });

    try {
      // CRITICAL DEBUG: Log what we're sending to help diagnose issues
      console.log('[BootcampUpsellPage] One-click checkout debug:', {
        userEmail: user?.email,
        paymentMethodId: defaultPaymentMethod?.id,
        selectedPlanKey,
        isAuthenticated,
      });

      // Validate user email is present - required for backend to find Stripe customer
      if (!user?.email) {
        throw new Error('User email not available. Please refresh the page and try again.');
      }

      // Get the plan details for the payment intent
      const selectedPlan = paymentPlans.find(p => p.planKey === selectedPlanKey);
      if (!selectedPlan) {
        throw new Error('Selected payment plan not found');
      }

      // Build terms payload - user accepted terms on previous Starter checkout
      const termsPayload = {
        docType: 'terms-of-service',
        version: new Date().toISOString().split('T')[0],
        acceptedAt: new Date().toISOString(),
      };

      // Use create-payment-intent endpoint for one-time purchases
      // This endpoint:
      // 1. Looks up existing customer by email (lines 947-965 in backend)
      // 2. Attaches payment method if needed (lines 982-989)
      // 3. Creates and confirms PaymentIntent for one-time charge
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.botspot.trade';
      const response = await fetchWithTimeout(`${API_URL}/stripe/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          paymentMethodId: defaultPaymentMethod.id,
          productSlugs: ['ai-bootcamp'],
          items: [{
            productSlug: 'ai-bootcamp',
            planKey: selectedPlanKey,
            quantity: 1,
          }],
          terms: termsPayload,
          promoCode: UPSELL_PROMO_CODE,
          flowContext: 'bootcamp_upsell_one_click',
        }),
      }, PAYMENT_INTENT_FETCH_TIMEOUT_MS);

      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.message || data.error || 'Payment failed');
      }

      // CRITICAL: Handle subscription with 'incomplete' status
      // When payment_behavior: 'default_incomplete' is used, Stripe creates the subscription
      // but doesn't auto-charge. We need to confirm the payment using Stripe.js.
      // The clientSecret is attached to the invoice's payment intent.
      if (data.status === 'incomplete' && data.clientSecret) {
        console.log('[BootcampUpsellPage] Subscription incomplete, confirming payment with Stripe.js');

        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Payment service not available. Please try again.');
        }

        // Confirm the payment using the saved card
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          data.clientSecret,
          {
            payment_method: defaultPaymentMethod.id,
          }
        );

        if (confirmError) {
          console.error('[BootcampUpsellPage] Payment confirmation failed:', confirmError);
          throw new Error(confirmError.message || 'Payment confirmation failed. Please try again.');
        }

        // Check payment intent status after confirmation
        if (paymentIntent.status !== 'succeeded' && paymentIntent.status !== 'processing') {
          throw new Error(`Payment not completed. Status: ${paymentIntent.status}`);
        }

        console.log('[BootcampUpsellPage] Payment confirmed successfully:', paymentIntent.id);

        // Update payment intent ID for tracking
        data.paymentIntentId = paymentIntent.id;
      }
      // Check if payment requires additional action (3D Secure redirect, etc.)
      else if (data.status === 'requires_action' || data.status === 'requires_confirmation') {
        // For cases that require redirect (unusual for one-click with saved card)
        throw new Error('Additional verification required. Please use the full checkout.');
      }

      // Track successful one-click purchase
      pushEvent({
        event: 'upsell_one_click_success',
        upsell_product: 'ai-bootcamp',
        upsell_price: planDetails?.mainPrice,
        upsell_plan: selectedPlanKey,
        payment_intent_id: data.paymentIntentId || data.subscriptionId,
        subscription_id: data.subscriptionId,
        time_on_page_seconds: timeOnPageSeconds,
        source: 'post_starter_checkout',
      });

      // Success! Navigate to AI Agent with success param
      setShowConfirmModal(false);
      const successUrl = new URL(skipRedirectUrl, window.location.origin);
      successUrl.searchParams.set('bootcamp_added', 'true');
      navigate(successUrl.pathname + successUrl.search, { replace: true });

    } catch (error) {
      console.error('[BootcampUpsellPage] One-click checkout failed:', error);
      setOneClickError(error.message || 'Payment failed. Please try again.');

      // Track failure
      pushEvent({
        event: 'upsell_one_click_failed',
        upsell_product: 'ai-bootcamp',
        upsell_plan: selectedPlanKey,
        error_message: error.message,
        source: 'post_starter_checkout',
      });
    } finally {
      setOneClickProcessing(false);
    }
  }, [defaultPaymentMethod, selectedPlanKey, getPlanPricingDetails, pageLoadTime, paymentPlans, skipRedirectUrl, navigate, user, isAuthenticated]);

  // Handle modal close
  const handleConfirmModalClose = useCallback(() => {
    if (!oneClickProcessing) {
      setShowConfirmModal(false);
      setOneClickError(null);
    }
  }, [oneClickProcessing]);

  // Handle decline upsell - continue to AI Agent
  const handleDeclineUpsell = useCallback(() => {
    const planDetails = getPlanPricingDetails(selectedPlanKey);
    const timeOnPageSeconds = Math.round((Date.now() - pageLoadTime) / 1000);

    pushEvent({
      event: 'upsell_declined',
      upsell_product: 'ai-bootcamp',
      source: 'post_starter_checkout',
      time_on_page_seconds: timeOnPageSeconds,
      last_viewed_plan: selectedPlanKey,
      last_viewed_price: planDetails?.mainPrice,
      redirect_destination: skipRedirectUrl,
    });

    navigate(skipRedirectUrl, { replace: true });
  }, [navigate, skipRedirectUrl, selectedPlanKey, getPlanPricingDetails, pageLoadTime]);

  const pricing = getBootcampPrice();

  // Transformation-focused features (Hormozi style: outcomes, not features)
  const transformationFeatures = [
    {
      icon: FaRocket,
      text: 'Become an AI trading master in 5 weeks',
      subtext: 'No coding required'
    },
    {
      icon: FaUsers,
      text: 'Join 2,400+ traders automating their income',
      subtext: 'Active community support'
    },
    {
      icon: FaTrophy,
      text: '87% deploy their first bot within 14 days',
      subtext: 'Based on 2025 cohort data'
    },
    {
      icon: FaShieldAlt,
      text: 'Risk-free: Full refund within 30 days',
      subtext: 'No questions asked'
    },
  ];

  // Value stack (Hormozi style: show bundled value totaling $5,500+)
  const valueStack = [
    { item: 'Live 5-Week Bootcamp', value: '$1,997' },
    { item: 'One-on-One Instructor Support', value: '$997' },
    { item: 'Lifetime Recording Access', value: '$497' },
    { item: '50+ Elite Strategies Library', value: '$997' },
    { item: 'Private Discord Community', value: '$497' },
    { item: 'Live Weekly Q&A Sessions', value: '$297' },
    { item: 'Strategy Templates & Code', value: '$297' },
  ];

  const totalStackValue = valueStack.reduce((sum, item) => {
    const num = parseInt(item.value.replace(/[$,]/g, ''), 10);
    return sum + (isNaN(num) ? 0 : num);
  }, 0);


  // Curriculum accordion state
  const [openWeekId, setOpenWeekId] = useState(null);

  // Simple curriculum data - same content as BootcampCurriculum fallback
  // Just the week accordion, no extra bloat
  const curriculumWeeks = [
    {
      week: 1,
      title: 'Getting Started with AI Trading',
      topics: [
        'Introduction to BotSpot platform and AI Bot Builder',
        'Setting up your development environment',
        'Understanding market data and trading basics',
        'Your first AI-generated trading strategy'
      ]
    },
    {
      week: 2,
      title: 'Stock Trading Strategies',
      topics: [
        'Technical indicators and their implementation',
        'Building mean reversion strategies',
        'Backtesting best practices',
        'Risk management fundamentals'
      ]
    },
    {
      week: 3,
      title: 'Crypto Trading Bots',
      topics: [
        'Crypto market structure and exchanges',
        'Building crypto-specific strategies',
        'Handling market volatility',
        'Position sizing and risk controls'
      ]
    },
    {
      week: 4,
      title: 'Options & Futures',
      topics: [
        'Options basics and Greeks',
        'Building options strategies (covered calls, spreads)',
        'Futures trading fundamentals',
        'Managing leverage and margin'
      ]
    },
    {
      week: 5,
      title: 'Deployment & Portfolio Management',
      topics: [
        'Deploying bots to live trading',
        'Portfolio diversification strategies',
        'Monitoring and maintenance',
        'Performance analysis and optimization'
      ]
    }
  ];

  // Toggle curriculum week accordion
  const toggleWeek = (weekNum) => {
    setOpenWeekId(openWeekId === weekNum ? null : weekNum);
  };

  // FAQ state for accordion
  const [openFaqId, setOpenFaqId] = useState(null);

  // Compact FAQs for upsell page (subset of main bootcamp FAQs)
  const upsellFaqs = [
    {
      id: 'requirements',
      question: 'Do I need coding experience?',
      answer: 'No coding experience required! Our AI Bot Builder uses natural language prompts, so you can create trading strategies by describing them in plain English.'
    },
    {
      id: 'schedule',
      question: 'What if I miss a live session?',
      answer: 'All sessions are recorded and available immediately. You get lifetime access to all recordings, so you can learn at your own pace.'
    },
    {
      id: 'guarantee',
      question: 'What if it\'s not right for me?',
      answer: 'We offer a 30-day money-back guarantee. If you\'re not satisfied within the first 30 days, we\'ll refund you in full—no questions asked.'
    },
    {
      id: 'deployment',
      question: 'Can I deploy bots right away?',
      answer: 'Yes! During the bootcamp, you\'ll deploy live bots to BotSpot.trade. By week 5, you\'ll have real trading strategies running with proper risk management.'
    }
  ];

  // Trustpilot reviews data - 4.6 stars, 18 reviews
  const trustpilotReviews = [
    {
      name: 'Robert MacLeod',
      location: 'US',
      date: 'Jun 2023',
      rating: 5,
      title: 'LumiWealth is an amazing community',
      text: 'I went 0 python to algorithm in what I feel is record time! The Founder Rob is a wonderful, kind, and passionate human. His enthusiasm for developers of all skill levels is impressive.',
    },
    {
      name: 'Ali',
      location: 'US',
      date: 'May 2024',
      rating: 5,
      title: 'Powerful Algorithms Made Simple',
      text: 'I have decades of experience in investing, option trading and software. I still find the Pro Plan of great value. It is a great pleasure to work with Robert in the algorithm development sessions.',
    },
    {
      name: 'Worth',
      location: 'US',
      date: 'Feb 2024',
      rating: 5,
      title: 'Excellent experience so far',
      text: "Robert's decades of experience in programming and finance are visible in the materials. He has the skills of a teacher and has been instrumental in the acceleration of my trading journey.",
    },
    {
      name: 'Charlie Murphy',
      location: 'US',
      date: 'Jan 2024',
      rating: 5,
      title: 'Professional grade code',
      text: 'I am a professional software engineer and the code I have seen is professional grade and easy to use. I would highly recommend Lumiwealth for even a non-coder to help develop a useful trading bot.',
    },
    {
      name: 'Dea Elmi',
      location: 'US',
      date: 'May 2023',
      rating: 5,
      title: 'Outstanding course',
      text: "Rob and his team are amazing teachers. The courses are concise, easy to follow and very practical. Additionally Rob and team are super nice, welcoming and accommodating.",
    },
    {
      name: 'Daniel Ogola',
      location: 'UG',
      date: 'Mar 2024',
      rating: 5,
      title: "I really like Robert's ways",
      text: "He is so calm and very knowledgeable about financial markets and programming. The ability to schedule a meeting with Lumiwealth before even making a single penny is one of those paramount things I've ever seen.",
    },
  ];

  // Reviews carousel state
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  // Auto-rotate reviews every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReviewIndex((prev) => (prev + 1) % trustpilotReviews.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [trustpilotReviews.length]);

  // Reviews navigation functions
  const goToNextReview = useCallback(() => {
    setCurrentReviewIndex((prev) => (prev + 1) % trustpilotReviews.length);
  }, [trustpilotReviews.length]);

  const goToPrevReview = useCallback(() => {
    setCurrentReviewIndex((prev) => (prev - 1 + trustpilotReviews.length) % trustpilotReviews.length);
  }, [trustpilotReviews.length]);

  // Touch swipe support for reviews carousel
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartX.current || !touchEndX.current) return;

    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Minimum swipe distance in pixels

    if (Math.abs(diff) > minSwipeDistance) {
      if (diff > 0) {
        // Swipe left - go to next review
        goToNextReview();
      } else {
        // Swipe right - go to previous review
        goToPrevReview();
      }
    }

    // Reset touch values
    touchStartX.current = null;
    touchEndX.current = null;
  }, [goToNextReview, goToPrevReview]);

  // Handle FAQ toggle with analytics
  const toggleFaq = (id) => {
    const isOpening = openFaqId !== id;
    setOpenFaqId(isOpening ? id : null);

    // Track FAQ interaction
    if (isOpening) {
      const faq = upsellFaqs.find(f => f.id === id);
      pushEvent({
        event: 'upsell_faq_expanded',
        upsell_product: 'ai-bootcamp',
        faq_id: id,
        faq_question: faq?.question,
        source: 'post_starter_checkout',
      });
    }
  };

  // Handle payment plan selection with analytics
  const handlePlanSelection = useCallback((planKey) => {
    const previousPlan = selectedPlanKey;
    setSelectedPlanKey(planKey);

    // Track plan change (only if actually changing)
    if (previousPlan && previousPlan !== planKey) {
      const planDetails = getPlanPricingDetails(planKey);
      pushEvent({
        event: 'upsell_payment_plan_changed',
        upsell_product: 'ai-bootcamp',
        previous_plan: previousPlan,
        new_plan: planKey,
        new_plan_price: planDetails?.mainPrice,
        new_plan_installments: planDetails?.installments,
        source: 'post_starter_checkout',
      });
    }
  }, [selectedPlanKey, getPlanPricingDetails]);

  return (
    <div className="bootcamp-upsell-page">
      <Container className="bootcamp-upsell-container">
        {/* Success confirmation banner */}
        <div className="upsell-success-banner">
          <FaCheckCircle className="success-icon" />
          <span>You're In! Your Starter Plan is Active</span>
        </div>

        {/* BRIDGE SECTION - Short transition to upsell */}
        <div className="upsell-bridge-section">
          <p className="bridge-text">
            🎁 Special offer for new subscribers
          </p>
        </div>

        <div className="upsell-card">
          {/* Two-column layout for desktop */}
          <div className="upsell-layout">
            {/* Left column: Hero + Features */}
            <div className="upsell-main-content">
              {/* Header */}
              <div className="upsell-header">
                <h1 className="upsell-title">
                  Expert help. Real results.
                </h1>
                <p className="upsell-subtitle">Join the AI Trading Bootcamp — the fast track for new traders</p>
              </div>

              {/* Bootcamp transformation image - visual break */}
              <div className="upsell-hero-image">
                <picture>
                  <source
                    srcSet="/images/bootcamp-transformation-1000.webp"
                    type="image/webp"
                  />
                  <img
                    src="/images/bootcamp-transformation-1000.jpg"
                    alt="Transform from manual trading to AI-powered automated trading in 5 weeks"
                    className="upsell-transformation-image"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    width="1000"
                    height="545"
                  />
                </picture>
              </div>

              {/* Transformation features (Hormozi style) - compact */}
              <ul className="upsell-features">
                {transformationFeatures.map((feature, index) => (
                  <li key={index} className="upsell-feature">
                    <feature.icon className="feature-icon" />
                    <div className="feature-content">
                      <span className="feature-text">{feature.text}</span>
                      {feature.subtext && (
                        <span className="feature-subtext">{feature.subtext}</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right column: CTA Card (above the fold!) */}
            <div className="upsell-cta-card" ref={ctaCardRef}>
              {/* Special offer badge */}
              <div className="upsell-special-offer-section">
                <div className="upsell-special-offer">
                  🎁 New Subscriber Special{(() => {
                    // Get discount percentage from selected plan's real API data
                    const selectedPlan = paymentPlans.find(p => p.planKey === selectedPlanKey);
                    if (selectedPlan?.discountedAmount && selectedPlan?.amount && selectedPlan.discountedAmount !== selectedPlan.amount) {
                      const discountPercent = Math.round((selectedPlan.amount - selectedPlan.discountedAmount) / selectedPlan.amount * 100);
                      return ` — Save ${discountPercent}%`;
                    }
                    return '';
                  })()}
                </div>
              </div>

              {/* Cohort date - when next bootcamp starts */}
              {!isCourseLoading && nextSessionDate?.date && nextSessionDate.date !== 'Date TBD' && (
                <div className="upsell-cohort-urgency">
                  <FaCalendarAlt className="urgency-icon" />
                  <span>
                    Next cohort starts: <strong>{nextSessionDate.date}</strong>
                    {nextSessionDate.dayOfWeek && nextSessionDate.dayOfWeek !== 'Day TBD' && (
                      <span className="cohort-day"> ({nextSessionDate.dayOfWeek}s at {nextSessionDate.time || 'TBD'})</span>
                    )}
                  </span>
                </div>
              )}

              {/* Payment Plan Selector - Uses REAL API prices */}
              {!isLoadingPlans && paymentPlans.length > 0 && selectedPlanKey && (
                <div className="upsell-payment-plans">
                  <div className="payment-plans-label">Choose your plan:</div>
                  <div className="payment-plan-options">
                    {paymentPlans.map((plan) => {
                      const isSelected = selectedPlanKey === plan.planKey;
                      // Use installments from plan data
                      const installments = plan.installments || plan.metadata?.installments || 1;
                      const isBestValue = installments === 1; // Pay in Full = Best Value

                      // Label pattern: "Pay in Full" or "X Monthly Payments"
                      const planLabel = installments === 1
                        ? 'Pay in Full'
                        : `${installments} Monthly Payments`;

                      // Use REAL API prices - discountedAmount if available, else amount (in cents)
                      // formatPrice expects CENTS and converts to dollars internally
                      const priceInCents = plan.discountedAmount ?? plan.amount;
                      const priceDisplay = installments === 1
                        ? formatPrice(priceInCents)
                        : `${formatPrice(priceInCents)}/mo`;

                      return (
                        <button
                          key={plan.planKey}
                          className={`payment-plan-option ${isSelected ? 'selected' : ''} ${isBestValue ? 'best-value' : ''}`}
                          onClick={() => handlePlanSelection(plan.planKey)}
                          type="button"
                        >
                          {isBestValue && <span className="plan-option-badge best-value">Best Value</span>}
                          <div className="plan-option-label">{planLabel}</div>
                          <div className="plan-option-price">{priceDisplay}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Pricing section - Real prices from API based on selected plan */}
              <div className="upsell-pricing">
                {isLoadingPricing || isLoadingPlans || pricing.isLoading ? (
                  <div className="price-loading">
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    Loading pricing...
                  </div>
                ) : (
                  <>
                    {(() => {
                      const planDetails = getPlanPricingDetails(selectedPlanKey);
                      if (planDetails) {
                        return (
                          <>
                            <div className="price-comparison">
                              {planDetails.originalPrice && (
                                <span className="original-price">{planDetails.originalPrice}</span>
                              )}
                              <span className="discount-price">{planDetails.mainPrice}</span>
                            </div>
                            <p className="price-note">{planDetails.subtext}</p>
                          </>
                        );
                      }
                      // Fallback to base pricing if no plan details
                      return (
                        <>
                          <div className="price-comparison">
                            {pricing.original && (
                              <span className="original-price">{pricing.original}</span>
                            )}
                            <span className="discount-price">{pricing.current || '$997'}</span>
                          </div>
                          <p className="price-note">One-time payment • Lifetime access</p>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>

              {/* Countdown timer - positioned near CTA for urgency at decision point */}
              {/* 15-minute timer optimal for post-purchase upsells - no hours display needed */}
              {timeRemaining && !timeRemaining.expired && (
                <div className="upsell-countdown">
                  <FaClock className="countdown-icon" />
                  <span className="countdown-label">Offer expires in:</span>
                  <div className="countdown-timer">
                    <span className="countdown-unit">
                      <span className="countdown-number">{String(timeRemaining.minutes).padStart(2, '0')}</span>
                      <span className="countdown-text">min</span>
                    </span>
                    <span className="countdown-separator">:</span>
                    <span className="countdown-unit">
                      <span className="countdown-number">{String(timeRemaining.seconds).padStart(2, '0')}</span>
                      <span className="countdown-text">sec</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Primary CTA - ABOVE THE FOLD */}
              <button
                className="upsell-cta-primary"
                onClick={handleAcceptUpsell}
                disabled={checkoutProcessing || oneClickProcessing || isLoadingPricing || isLoadingPlans}
              >
                {checkoutProcessing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <FaPlay className="me-2" />
                    Add to My Order
                  </>
                )}
              </button>

              {/* Skip link */}
              <button
                className="upsell-skip-link"
                onClick={handleDeclineUpsell}
                disabled={checkoutProcessing || oneClickProcessing}
              >
                {skipRedirectLabel}
              </button>
            </div>
          </div>

          {/* Trustpilot Reviews Section - Social proof right after CTA */}
          <div className="upsell-reviews-section">
            <div className="reviews-header">
              <div className="trustpilot-badge">
                <div className="stars-row">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className={`star ${star <= 4 ? 'filled' : 'partial'}`}>★</span>
                  ))}
                </div>
                <span className="rating-text">4.6 out of 5</span>
              </div>
              <a
                href="https://ca.trustpilot.com/review/lumiwealth.com"
                target="_blank"
                rel="noopener noreferrer"
                className="trustpilot-link"
              >
                <span className="trustpilot-logo">Trustpilot</span>
              </a>
            </div>

            <div
              className="reviews-carousel"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Left arrow */}
              <button
                className="carousel-arrow carousel-arrow-left"
                onClick={goToPrevReview}
                aria-label="Previous review"
              >
                <FaChevronLeft />
              </button>

              <div className="review-card">
                <div className="review-stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className="star filled">★</span>
                  ))}
                </div>
                <h5 className="review-title">"{trustpilotReviews[currentReviewIndex].title}"</h5>
                <p className="review-text">{trustpilotReviews[currentReviewIndex].text}</p>
                <div className="review-author">
                  <span className="author-name">{trustpilotReviews[currentReviewIndex].name}</span>
                  <span className="author-meta">{trustpilotReviews[currentReviewIndex].location} • {trustpilotReviews[currentReviewIndex].date}</span>
                </div>
              </div>

              {/* Right arrow */}
              <button
                className="carousel-arrow carousel-arrow-right"
                onClick={goToNextReview}
                aria-label="Next review"
              >
                <FaChevronRight />
              </button>

              <div className="carousel-nav">
                <div className="carousel-dots">
                  {trustpilotReviews.map((_, index) => (
                    <button
                      key={index}
                      className={`dot ${index === currentReviewIndex ? 'active' : ''}`}
                      onClick={() => setCurrentReviewIndex(index)}
                      aria-label={`Go to review ${index + 1}`}
                    />
                  ))}
                </div>
                <span className="carousel-counter">{currentReviewIndex + 1} / {trustpilotReviews.length}</span>
              </div>
            </div>
          </div>

          {/* Value Stack + Curriculum + Social Proof (below the fold, supporting content) */}
          <div className="upsell-supporting-content">
            {/* Value Stack (Hormozi style) */}
            <div className="upsell-value-stack">
              <h4 className="value-stack-title">
                <FaGraduationCap className="me-2" />
                Everything You Get:
              </h4>
              <div className="value-stack-items">
                {valueStack.map((item, index) => (
                  <div key={index} className="value-stack-item">
                    <FaCheck className="value-check" />
                    <span className="item-name">{item.item}</span>
                    <span className="item-value">{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="value-stack-total">
                <span className="total-label">Total Value:</span>
                <span className="total-value">${totalStackValue.toLocaleString()}</span>
              </div>
              {/* Show actual price based on selected payment plan */}
              {selectedPlanKey && (() => {
                const planDetails = getPlanPricingDetails(selectedPlanKey);
                if (!planDetails) return null;
                return (
                  <div className="value-stack-your-price">
                    <span className="your-price-label">Your Price:</span>
                    <span className="your-price-value">
                      {planDetails.installments > 1
                        ? `Only ${planDetails.installments} × ${planDetails.perPayment}`
                        : `Only ${planDetails.mainPrice}`
                      }
                    </span>
                  </div>
                );
              })()}
            </div>

            {/* Curriculum section - simple week accordion */}
            <div className="upsell-curriculum">
              <h4 className="curriculum-title">
                <FaBook className="me-2" />
                5-Week Curriculum
              </h4>
              <div className="curriculum-weeks">
                {curriculumWeeks.map((week) => (
                  <div
                    key={week.week}
                    className={`curriculum-week ${openWeekId === week.week ? 'expanded' : ''}`}
                  >
                    <button
                      className="week-header"
                      onClick={() => toggleWeek(week.week)}
                      type="button"
                    >
                      <span className="week-number">Week {week.week}</span>
                      <span className="week-title">{week.title}</span>
                      {openWeekId === week.week ? (
                        <FaChevronUp className="week-chevron" />
                      ) : (
                        <FaChevronDown className="week-chevron" />
                      )}
                    </button>
                    {openWeekId === week.week && (
                      <ul className="week-topics">
                        {week.topics.map((topic, idx) => (
                          <li key={idx}>{topic}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Mid-page CTA - after value stack and curriculum */}
            <div className="upsell-mid-cta">
              <button
                className="upsell-cta-primary"
                onClick={handleAcceptUpsell}
                disabled={checkoutProcessing || oneClickProcessing || isLoadingPricing || isLoadingPlans}
              >
                {checkoutProcessing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <FaPlay className="me-2" />
                    Add to My Order
                  </>
                )}
              </button>
              <button
                className="upsell-skip-link"
                onClick={handleDeclineUpsell}
                disabled={checkoutProcessing || oneClickProcessing}
              >
                {skipRedirectLabel}
              </button>
            </div>
          </div>

          {/* Social proof - separate section */}
          <div className="upsell-social-proof-section">
            <div className="upsell-testimonial">
              <p className="testimonial-quote">"87% of bootcamp graduates deploy their first live bot within 14 days"</p>
              <p className="testimonial-attribution">Based on 2025 cohort data</p>
            </div>
          </div>

          {/* What happens next - Clarity section */}
          <div className="upsell-what-happens-next">
            <h4 className="what-happens-title">
              <FaCheckCircle className="me-2" />
              What Happens After You Add This:
            </h4>
            <ol className="what-happens-list">
              <li>
                <span className="step-number">1.</span>
                <span className="step-text">Schedule a kickoff call with Robert, our CEO, to create your personalized learning plan</span>
              </li>
              <li>
                <span className="step-number">2.</span>
                <span className="step-text">Get instant access to all previously recorded bootcamp sessions so you can start learning immediately</span>
              </li>
              <li>
                <span className="step-number">3.</span>
                <span className="step-text">Receive your Zoom link to join the next live cohort session and start building your first bot</span>
              </li>
            </ol>
          </div>

          {/* FAQ Section - Compact accordion */}
          <div className="upsell-faq-section">
            <h4 className="faq-section-title">
              <FaQuestionCircle className="me-2" />
              Common Questions
            </h4>
            <div className="faq-items">
              {upsellFaqs.map((faq) => (
                <div
                  key={faq.id}
                  className={`faq-item ${openFaqId === faq.id ? 'open' : ''}`}
                >
                  <button
                    className="faq-question"
                    onClick={() => toggleFaq(faq.id)}
                    type="button"
                  >
                    <span>{faq.question}</span>
                    {openFaqId === faq.id ? (
                      <FaChevronUp className="faq-chevron" />
                    ) : (
                      <FaChevronDown className="faq-chevron" />
                    )}
                  </button>
                  {openFaqId === faq.id && (
                    <div className="faq-answer">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Secondary CTA at bottom - centered with skip link */}
          <div className="upsell-bottom-cta">
            <button
              className="upsell-cta-primary"
              onClick={handleAcceptUpsell}
              disabled={checkoutProcessing || oneClickProcessing || isLoadingPricing || isLoadingPlans}
            >
              {checkoutProcessing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Processing...
                </>
              ) : (
                <>
                  <FaPlay className="me-2" />
                  Add to My Order — {getPlanPricingDetails(selectedPlanKey)?.mainPrice || pricing.current || '$997'}
                </>
              )}
            </button>
            <button
              className="upsell-skip-link"
              onClick={handleDeclineUpsell}
              disabled={checkoutProcessing || oneClickProcessing}
            >
              {skipRedirectLabel}
            </button>
          </div>
        </div>
      </Container>

      {/* One-Click Checkout Confirmation Modal */}
      {/* Research shows confirmation modal reduces chargebacks from 15% to 5% while only */}
      {/* reducing conversion by 0.2% - significantly higher net revenue */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleConfirmModalClose}
        onConfirm={handleOneClickConfirm}
        title="Confirm Your Bootcamp Enrollment"
        confirmText={oneClickProcessing ? 'Processing...' : 'Yes, Enroll Me'}
        cancelText="No Thanks"
        variant="primary"
        isProcessing={oneClickProcessing}
        confirmDisabled={oneClickProcessing}
        className="bootcamp-upsell-modal"
      >
        <div className="upsell-confirm-modal-content">
          {/* Product summary */}
          <div className="confirm-modal-product">
            <FaGraduationCap className="confirm-modal-icon" />
            <div className="confirm-modal-product-info">
              <h4>AI Trading Bootcamp</h4>
              <p className="confirm-modal-plan">
                {(() => {
                  const plan = paymentPlans.find(p => p.planKey === selectedPlanKey);
                  // Must check both plan.installments and plan.metadata.installments
                  // (same pattern as getPlanPricingDetails)
                  const installments = plan?.installments || plan?.metadata?.installments || 1;
                  return installments === 1 ? 'Pay in Full' : `${installments}-Month Payment Plan`;
                })()}
              </p>
            </div>
          </div>

          {/* Price */}
          <div className="confirm-modal-price">
            <span className="confirm-modal-price-label">Total:</span>
            <span className="confirm-modal-price-value">
              {getPlanPricingDetails(selectedPlanKey)?.mainPrice || pricing.current || '$997'}
            </span>
          </div>

          {/* Payment method */}
          {defaultPaymentMethod && (
            <div className="confirm-modal-payment-method">
              <FaShieldAlt className="confirm-modal-card-icon" />
              <span>
                Charging {defaultPaymentMethod.brand} ending in {defaultPaymentMethod.last4}
              </span>
            </div>
          )}

          {/* Error message */}
          {oneClickError && (
            <div className="confirm-modal-error">
              {oneClickError}
            </div>
          )}

          {/* Guarantee reminder */}
          <div className="confirm-modal-guarantee">
            <FaCheckCircle className="confirm-modal-guarantee-icon" />
            <span>30-day money-back guarantee</span>
          </div>
        </div>
      </ConfirmationModal>

      {/* Sticky Bottom CTA - Mobile Only */}
      {/* Research shows sticky CTAs improve post-purchase upsell conversion by 8-25% */}
      {/* Hidden when main CTA card scrolls into view to avoid duplicate buttons */}
      <div className={`upsell-sticky-cta ${showStickyCta ? 'visible' : 'hidden'}`}>
        <button
          className="sticky-cta-button"
          onClick={handleAcceptUpsell}
          disabled={checkoutProcessing || oneClickProcessing || isLoadingPricing || isLoadingPlans}
        >
          {checkoutProcessing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Processing...
            </>
          ) : (
            <>
              <FaPlay className="me-2" />
              Add to My Order — {getPlanPricingDetails(selectedPlanKey)?.mainPrice || pricing.current || '$997'}
            </>
          )}
        </button>
        <button
          className="sticky-skip-link"
          onClick={handleDeclineUpsell}
          disabled={checkoutProcessing || oneClickProcessing}
        >
          No thanks, continue to AI Agent →
        </button>
      </div>
    </div>
  );
}

export default BootcampUpsellPage;
